/**
 * ImportListModal - Import cards from text, JSON, or CSV
 *
 * Uses shared useImportSteps for 4-step flow:
 *   input → preview → importing → complete
 *
 * Supports: MtgCraft backups, Dragon Shield/DelverLens/Moxfield CSV,
 * MTGA/Moxfield/MTGO deck text, simple text lists, CSV spreadsheets.
 */
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { ChangeEvent, ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FaArrowRight, FaCheck, FaCheckCircle, FaExclamationTriangle, FaFileImport, FaInfoCircle, FaTimes } from 'react-icons/fa';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';
import type { DeckImportCard } from '@/features/decks/lib/deckImportExport';
import { parseDeckList } from '@/features/decks/lib/deckImportExport';
import FileDropzone from '@/features/import-export/components/FileDropzone';
import ImportTabs from '@/features/import-export/components/ImportTabs';
import SupportedFormatsInfo from '@/features/import-export/components/SupportedFormatsInfo';
import { deckBackupToListCards, deckCardsToListCards, importCardsToListCards } from '@/features/import-export/lib/formatAdapters';
import { detectMtgCraftBackupType, isMtgCraftBackup, parseDeckBackup } from '@/features/import-export/parsers/mtgcraft';
import { detectFormat, detectFormatFull, parseImport } from '@/features/import-export/parsers/parseImport';
import type { ImportTabId } from '@/features/import-export/types';
import useCardIndex from '@/hooks/useCardIndex';
import useImportSteps from '@/hooks/useImportSteps';
import { parseUniversalCSV } from '@/lib/universalCSVParser';
import type { MinimalCard } from '@/services/cardDatabase';

import { useAddListCardsBatch, useCreateList, useLists } from '../hooks/api';
import { listStatsByListAtom } from '../state/enrichedListCards';
import { mergeDuplicateCards, parseListImport, type ListImportFormat, type ParsedListCard, type ParsedListMeta } from '../utils/parsers';

interface ImportListModalProps {
  isOpen: boolean;
  onClose: () => void;
  listId?: string;
  onMultiListDetected?: () => void;
}

interface MatchedCard {
  parsed: ParsedListCard;
  card: MinimalCard | null;
  status: 'found' | 'not-found' | 'ambiguous';
}

const FORMAT_LABELS: Record<ListImportFormat | 'deck-text' | 'deck-backup' | 'deck-csv' | 'collection-csv', string> = {
  mtgcraft: 'MtgCraft Backup',
  'mtgcraft-multi': 'MtgCraft Multi-List Backup',
  text: 'Card List - Text',
  csv: 'CSV Spreadsheet',
  'deck-text': 'Deck List',
  'deck-backup': 'MtgCraft Deck Backup',
  'deck-csv': 'Deck CSV',
  'collection-csv': 'Collection CSV',
};

const SUPPORTED_FORMATS = [
  'MtgCraft Backup',
  'MtgCraft Deck Backup',
  'Collection CSV (Dragon Shield, DelverLens, Moxfield)',
  'Deck CSV (Dragon Shield, DelverLens Moxfield)',
  'Deck List (MTGA/Moxfield/MTGO)',
  'Text List',
  'CSV Spreadsheet',
];

const IMPORT_STEPS = ['input', 'preview', 'importing', 'complete'] as const;

export default function ImportListModal({ isOpen, onClose, listId, onMultiListDetected }: ImportListModalProps): ReactElement {
  const steps = useImportSteps(IMPORT_STEPS);

  // Input state
  const [activeTab, setActiveTab] = useState<ImportTabId>('upload');
  const [text, setText] = useState('');
  const [detectedFormat, setDetectedFormat] = useState<
    ListImportFormat | 'deck-text' | 'deck-backup' | 'deck-csv' | 'collection-csv' | null
  >(null);
  const [parsedMeta, setParsedMeta] = useState<ParsedListMeta | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [crossImportInfo, setCrossImportInfo] = useState<string | null>(null);

  // List selector state
  const [selectedListId, setSelectedListId] = useState(listId ?? '');
  const [createNewList, setCreateNewList] = useState(false);
  const [newListName, setNewListName] = useState('');

  // Progress / result state
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<{ success: number; failed: number; newListId?: string } | null>(null);

  const { data: lists = [] } = useLists();
  const statsByList = useAtomValue(listStatsByListAtom);
  const addCardsBatch = useAddListCardsBatch();
  const createList = useCreateList();
  const { getCardByName, getCardById, getCardByOracleId, getCardBySetNumber, searchCardsByPrefix, loading: dbLoading } = useCardIndex();

  // Parse text whenever it changes
  const parsedCards = useMemo((): ParsedListCard[] => {
    if (!text.trim()) {
      setDetectedFormat(null);
      setParsedMeta(null);
      setParseErrors([]);
      setCrossImportInfo(null);
      return [];
    }

    const trimmed = text.trim();
    const cardIndex = { getCardById, getCardByName, getCardByOracleId };

    if (isMtgCraftBackup(trimmed)) {
      const backupType = detectMtgCraftBackupType(trimmed);

      if (backupType === 'multi_list') {
        setDetectedFormat('mtgcraft-multi');
        setParsedMeta(null);
        setParseErrors([]);
        setCrossImportInfo('Multi-list backup detected. Use the multi-list import to preserve list structure.');
        return [];
      }

      if (backupType === 'single_deck' || backupType === 'multi_deck') {
        const backupResult = parseDeckBackup(trimmed);
        if (backupResult.decks.length > 0) {
          const allCards = backupResult.decks.flatMap((d) => d.cards);
          const listCards = deckBackupToListCards(allCards, cardIndex);
          setDetectedFormat('deck-backup');
          setParsedMeta(backupResult.decks.length === 1 ? { name: backupResult.decks[0].name, icon: null, color: null } : null);
          setParseErrors(backupResult.errors);
          setCrossImportInfo('Detected MtgCraft deck backup — importing as list entries (zone and printing info will be discarded).');
          return mergeDuplicateCards(listCards);
        }
      }
    }

    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      const collectionFormat = detectFormat(trimmed);
      if (collectionFormat) {
        const result = parseImport(trimmed, collectionFormat);
        if (result.cards.length > 0) {
          const listCards = importCardsToListCards(result.cards, cardIndex);
          setDetectedFormat('collection-csv');
          setParsedMeta(null);
          setParseErrors(result.errors);
          setCrossImportInfo(
            `Detected ${collectionFormat === 'dragonshield' ? 'Dragon Shield' : collectionFormat === 'delverlens' || collectionFormat === 'delverlens-legacy' ? 'DelverLens' : collectionFormat === 'moxfield' ? 'Moxfield' : 'MTGA'} collection format — importing as list entries (set, condition, and price info will be discarded).`,
          );
          return mergeDuplicateCards(listCards);
        }
      }

      if (!collectionFormat) {
        const deckDetected = detectFormatFull(trimmed, 'deck');
        if (deckDetected && deckDetected.inputType === 'csv') {
          const csvResult = parseUniversalCSV(trimmed, deckDetected.source);
          if (csvResult.cards.length > 0) {
            const deckCards: DeckImportCard[] = csvResult.cards.map((c) => ({
              name: c.name,
              setCode: c.setCode,
              collectorNumber: c.collectorNumber,
              quantity: c.quantity,
              zone: 'main' as const,
            }));
            const listCards = deckCardsToListCards(deckCards, cardIndex);
            setDetectedFormat('deck-csv');
            setParsedMeta(null);
            setParseErrors([]);
            setCrossImportInfo('Detected deck CSV — importing as list entries (zone info will be discarded).');
            return mergeDuplicateCards(listCards);
          }
        }
      }

      const deckResult = parseDeckList(trimmed);
      if (deckResult.cards.length > 0 && (deckResult.hasZoneDefinitions || deckResult.name !== null || deckResult.format !== null)) {
        const listCards = deckCardsToListCards(deckResult.cards, cardIndex);
        setDetectedFormat('deck-text');
        setParsedMeta(deckResult.name ? { name: deckResult.name, icon: null, color: null } : null);
        setParseErrors(deckResult.errors);
        setCrossImportInfo(
          deckResult.hasZoneDefinitions
            ? 'Detected deck list format — importing as list entries (zone info will be discarded).'
            : 'Detected deck text format — importing as list entries.',
        );
        return mergeDuplicateCards(listCards);
      }
    }

    setCrossImportInfo(null);
    const result = parseListImport(text);
    setDetectedFormat(result.format);
    setParsedMeta(result.listMeta ?? null);
    setParseErrors(result.errors);
    return mergeDuplicateCards(result.cards);
  }, [text, getCardById, getCardByName, getCardByOracleId]);

  // Match parsed cards against database
  const matchedCards = useMemo((): MatchedCard[] => {
    return parsedCards.map((parsed) => {
      if (parsed.setCode && parsed.collectorNumber) {
        const setMatch = getCardBySetNumber(parsed.setCode, parsed.collectorNumber);
        if (setMatch) return { parsed, card: setMatch, status: 'found' as const };
      }

      const exactMatch = getCardByName(parsed.name);
      if (exactMatch) return { parsed, card: exactMatch, status: 'found' as const };

      const searchResults = searchCardsByPrefix(parsed.name, 5);
      if (searchResults.length === 1) return { parsed, card: searchResults[0], status: 'found' as const };
      if (searchResults.length > 1) {
        const exact = searchResults.find((c) => c.name.toLowerCase() === parsed.name.toLowerCase());
        if (exact) return { parsed, card: exact, status: 'found' as const };
        return { parsed, card: searchResults[0], status: 'ambiguous' as const };
      }

      return { parsed, card: null, status: 'not-found' as const };
    });
  }, [parsedCards, getCardByName, getCardBySetNumber, searchCardsByPrefix]);

  const foundCount = matchedCards.filter((m) => m.status === 'found').length;
  const ambiguousCount = matchedCards.filter((m) => m.status === 'ambiguous').length;
  const notFoundCount = matchedCards.filter((m) => m.status === 'not-found').length;
  const selectedList = lists.find((l) => l.id === selectedListId);
  const canCreateFromBackup = (detectedFormat === 'mtgcraft' && parsedMeta?.name) || false;
  const canCreateFromTextImport =
    detectedFormat === 'deck-text' ||
    detectedFormat === 'deck-backup' ||
    detectedFormat === 'deck-csv' ||
    detectedFormat === 'collection-csv' ||
    detectedFormat === 'text' ||
    detectedFormat === 'csv';

  const handleFileDrop = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) setText(content);
    };
    reader.onerror = () => setParseErrors(['Failed to read file']);
    reader.readAsText(file);
  }, []);

  const handleTextChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  }, []);

  const handleImport = useCallback(async () => {
    const targetListId = createNewList ? null : selectedListId;
    if (!targetListId && !createNewList) return;
    if (matchedCards.length === 0) return;

    steps.goTo('importing');
    const validCards = matchedCards.filter((m) => m.card && m.card.oracle_id);
    const failedCount = matchedCards.length - validCards.length;
    setImportProgress({ current: 0, total: validCards.length });

    let newListId: string | undefined;

    try {
      let listIdToUse = targetListId;
      if (createNewList) {
        const listName = parsedMeta?.name ?? (newListName.trim() || 'Imported List');
        newListId = await createList({
          name: listName,
          icon: parsedMeta?.icon ?? undefined,
          color: parsedMeta?.color ?? undefined,
        });
        listIdToUse = newListId;
      }

      if (!listIdToUse) throw new Error('No list ID available');

      const cardsToAdd = validCards
        .filter((match) => match.card!.oracle_id != null)
        .map((match) => ({
          list_id: listIdToUse!,
          oracle_id: match.card!.oracle_id!,
          name: match.card!.name,
          scryfall_id: match.parsed.scryfall_id ?? match.card!.id,
          quantity: match.parsed.quantity,
          priority: match.parsed.priority ?? ('medium' as const),
          notes: match.parsed.notes ?? undefined,
        }));

      await addCardsBatch(cardsToAdd, (committed) => {
        setImportProgress({ current: committed, total: validCards.length });
      });

      setImportResults({ success: validCards.length, failed: failedCount, newListId });
    } catch (error) {
      console.error('Import failed:', error);
      setImportResults({ success: 0, failed: matchedCards.length, newListId });
    }

    steps.goTo('complete');
  }, [selectedListId, createNewList, parsedMeta, newListName, matchedCards, addCardsBatch, createList, steps]);

  const resetState = useCallback(() => {
    steps.reset();
    setText('');
    setDetectedFormat(null);
    setParsedMeta(null);
    setParseErrors([]);
    setSelectedListId(listId ?? '');
    setCreateNewList(false);
    setNewListName('');
    setImportResults(null);
    setActiveTab('upload');
    setCrossImportInfo(null);
    setImportProgress({ current: 0, total: 0 });
  }, [steps, listId]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const uploadContent = (
    <div className="space-y-4">
      <FileDropzone onFileDrop={handleFileDrop} accept=".json,.txt,.csv" description="Supports JSON, TXT, and CSV files" />
      <SupportedFormatsInfo formats={SUPPORTED_FORMATS} />
    </div>
  );

  const pasteContent = (
    <div className="space-y-4">
      <textarea
        value={text}
        onChange={handleTextChange}
        placeholder={`Paste your card list here...

Example formats:
4 Lightning Bolt
2x Counterspell
Sol Ring
Brainstorm x3`}
        rows={8}
        className="border-storm bg-slate/50 text-pearl placeholder-ash focus:border-aether w-full resize-y rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none"
      />
      <SupportedFormatsInfo formats={SUPPORTED_FORMATS} />
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Cards to List" size="lg" showCloseButton={!steps.isStep('importing')}>
      {/* Step: Input */}
      {steps.isStep('input') && (
        <div className="space-y-5">
          <ImportTabs activeTab={activeTab} onTabChange={setActiveTab} uploadContent={uploadContent} pasteContent={pasteContent} />

          {crossImportInfo && (
            <div className="bg-aether/10 border-aether/30 flex items-start gap-3 rounded-lg border p-3">
              <FaInfoCircle className="text-aether mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="text-silver text-sm">{crossImportInfo}</p>
                {detectedFormat === 'mtgcraft-multi' && onMultiListDetected && (
                  <button
                    type="button"
                    onClick={onMultiListDetected}
                    className="text-aether hover:text-aether/80 mt-2 text-sm font-medium underline transition-colors"
                  >
                    Switch to Multi-List Import
                  </button>
                )}
              </div>
            </div>
          )}

          {detectedFormat && (
            <div className="text-ash flex items-center gap-2 text-sm">
              <span>Format detected:</span>
              <span className="bg-storm text-silver rounded px-2 py-0.5 text-xs uppercase">
                {FORMAT_LABELS[detectedFormat] ?? detectedFormat}
              </span>
              {parsedMeta?.name && <span className="text-ash">· List: "{parsedMeta.name}"</span>}
            </div>
          )}

          {matchedCards.length > 0 && (
            <div className="flex gap-3 text-sm">
              {foundCount > 0 && (
                <span className="text-success">
                  <span className="font-semibold">{foundCount}</span> found
                </span>
              )}
              {ambiguousCount > 0 && (
                <span className="text-gold">
                  <span className="font-semibold">{ambiguousCount}</span> ambiguous
                </span>
              )}
              {notFoundCount > 0 && (
                <span className="text-danger">
                  <span className="font-semibold">{notFoundCount}</span> not found
                </span>
              )}
            </div>
          )}

          {parseErrors.length > 0 && (
            <div className="bg-danger/10 border-danger/30 rounded-lg border p-3">
              <div className="text-danger mb-1 flex items-center gap-2 text-sm font-medium">
                <FaExclamationTriangle className="h-4 w-4" />
                <span>Import warnings</span>
              </div>
              <ul className="text-danger/80 list-inside list-disc text-xs">
                {parseErrors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {parseErrors.length > 5 && <li>...and {parseErrors.length - 5} more</li>}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button text="Cancel" variant="text" onClick={handleClose} />
            <Button
              text={`Continue with ${matchedCards.length > 0 ? `${matchedCards.length} Cards` : 'Cards'}`}
              icon={<FaArrowRight />}
              onClick={() => steps.goTo('preview')}
              disabled={matchedCards.length === 0 || foundCount === 0 || dbLoading}
            />
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {steps.isStep('preview') && (
        <div className="space-y-5">
          {/* List selector */}
          <div className="space-y-3">
            {canCreateFromBackup && (
              <label
                className={clsx(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                  createNewList ? 'border-aether bg-aether/10' : 'border-storm hover:border-ash',
                )}
              >
                <input
                  type="checkbox"
                  checked={createNewList}
                  onChange={(e) => setCreateNewList(e.target.checked)}
                  className="accent-aether h-4 w-4"
                />
                <div>
                  <p className="text-pearl text-sm font-medium">Create new list from backup</p>
                  <p className="text-ash text-xs">
                    Creates "{parsedMeta?.name}" with {parsedMeta?.icon || '📋'} icon
                    {parsedMeta?.color && (
                      <span className="ml-1 inline-block h-3 w-3 rounded" style={{ backgroundColor: parsedMeta.color }} />
                    )}
                  </p>
                </div>
              </label>
            )}

            {!canCreateFromBackup && canCreateFromTextImport && (
              <label
                className={clsx(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                  createNewList ? 'border-aether bg-aether/10' : 'border-storm hover:border-ash',
                )}
              >
                <input
                  type="checkbox"
                  checked={createNewList}
                  onChange={(e) => setCreateNewList(e.target.checked)}
                  className="accent-aether h-4 w-4"
                />
                <div className="flex-1">
                  <p className="text-pearl text-sm font-medium">Create new list</p>
                  {createNewList && (
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder={parsedMeta?.name ?? 'List name...'}
                      className="border-storm bg-slate text-pearl placeholder-ash focus:border-aether mt-2 w-full rounded border px-2 py-1 text-sm focus:outline-none"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="New list name"
                    />
                  )}
                </div>
              </label>
            )}

            {!createNewList && (
              <div>
                <label htmlFor="list-select" className="text-silver mb-1 block text-sm">
                  Add to existing list
                </label>
                <select
                  id="list-select"
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                  className="bg-slate border-storm text-pearl focus:border-aether w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                >
                  <option value="">Select a list...</option>
                  {lists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({statsByList.get(list.id)?.cardCount ?? 0} cards)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Matched card preview */}
          <div className="border-storm rounded-lg border">
            <div className="border-storm flex items-center justify-between border-b px-3 py-2">
              <span className="text-silver text-sm font-medium">Preview ({matchedCards.length} cards)</span>
              <div className="flex gap-3 text-xs">
                {foundCount > 0 && <span className="text-success">{foundCount} found</span>}
                {ambiguousCount > 0 && <span className="text-gold">{ambiguousCount} ambiguous</span>}
                {notFoundCount > 0 && <span className="text-danger">{notFoundCount} not found</span>}
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {matchedCards.map((match, index) => (
                <div
                  key={index}
                  className={clsx(
                    'border-storm/50 flex items-center gap-2 border-b px-3 py-2 text-sm last:border-0',
                    match.status === 'not-found' && 'bg-danger/5',
                    match.status === 'ambiguous' && 'bg-gold/5',
                  )}
                >
                  {match.status === 'found' && <FaCheck className="text-success h-3 w-3 shrink-0" />}
                  {match.status === 'ambiguous' && <FaExclamationTriangle className="text-gold h-3 w-3 shrink-0" />}
                  {match.status === 'not-found' && <FaTimes className="text-danger h-3 w-3 shrink-0" />}
                  <span className="text-ash w-8 shrink-0 text-center font-mono">{match.parsed.quantity}×</span>
                  <span className={match.status === 'found' ? 'text-pearl' : 'text-silver'}>{match.card?.name ?? match.parsed.name}</span>
                  {match.parsed.priority && match.parsed.priority !== 'medium' && (
                    <span
                      className={clsx(
                        'rounded px-1.5 py-0.5 text-xs',
                        match.parsed.priority === 'high' && 'bg-danger/20 text-danger',
                        match.parsed.priority === 'low' && 'bg-slate text-ash',
                      )}
                    >
                      {match.parsed.priority}
                    </span>
                  )}
                  {match.status === 'ambiguous' && <span className="text-gold text-xs">(best match)</span>}
                  {match.status === 'not-found' && <span className="text-danger text-xs">(not found)</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-2">
            <Button text="Back" variant="text" onClick={() => steps.goTo('input')} />
            <Button
              text={`Import ${foundCount > 0 ? `${foundCount} Cards` : 'Cards'}`}
              icon={<FaFileImport />}
              color="create"
              onClick={handleImport}
              disabled={(!selectedListId && !createNewList) || foundCount === 0 || dbLoading}
            />
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {steps.isStep('importing') && (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="text-pearl mt-4 text-lg font-medium">Importing cards...</p>
          <p className="text-ash mt-2">
            {importProgress.current} of {importProgress.total} cards
          </p>
          <div className="bg-slate mt-4 h-2 w-64 overflow-hidden rounded-full">
            <div
              className="bg-aether h-full transition-all duration-300"
              style={{
                width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {steps.isStep('complete') && importResults && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-success/20 mb-4 rounded-full p-4">
            <FaCheckCircle className="text-success h-12 w-12" />
          </div>
          <h3 className="text-pearl text-xl font-semibold">Import Complete!</h3>
          <p className="text-silver mt-2">
            Added <span className="text-success font-medium">{importResults.success}</span> cards
            {importResults.newListId ? (
              <> to new list "{parsedMeta?.name ?? (newListName.trim() || 'Imported List')}"</>
            ) : (
              <> to {selectedList?.name}</>
            )}
          </p>
          {importResults.failed > 0 && (
            <p className="text-danger mt-1 text-sm">
              {importResults.failed} card{importResults.failed !== 1 ? 's' : ''} could not be imported
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <Button text="Import More" variant="outlined" onClick={resetState} />
            <Button text="Done" onClick={handleClose} />
          </div>
        </div>
      )}
    </Modal>
  );
}
